import React, { useEffect, useRef, useState } from 'react';
import { Send, Check, X, Mail } from 'lucide-react';

type Status = 'idle' | 'submitting' | 'sent' | 'error';

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const openerRef = useRef<HTMLButtonElement>(null);

  // Sync React state with the native <dialog> element so Esc/close events close cleanly.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleClose = () => {
      setOpen(false);
      setStatus('idle');
      formRef.current?.reset();
      openerRef.current?.focus();
    };
    dialog.addEventListener('close', handleClose);
    return () => dialog.removeEventListener('close', handleClose);
  }, []);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    // native <dialog> fires click on the backdrop with target === dialog
    if (e.target === dialogRef.current) setOpen(false);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'submitting') return;
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    setStatus('submitting');
    window.setTimeout(() => setStatus('sent'), 600);
  };

  const submitting = status === 'submitting';
  const sent = status === 'sent';

  return (
    <section className="contact-section" id="contact" aria-labelledby="contact-heading">
      <div className="contact-inner">
        <div className="contact-header">
          <span className="eyebrow reveal">Get in touch</span>
          <h2 id="contact-heading" className="reveal">We&rsquo;re listening.</h2>
          <p className="reveal">
            Tell us a bit about you, and we&rsquo;ll connect you with the right person on our team.
          </p>
          <div className="contact-cta reveal">
            <button
              ref={openerRef}
              type="button"
              className="btn btn-primary"
              onClick={() => setOpen(true)}
            >
              Start a conversation
              <Mail className="btn-icon" strokeWidth={1.5} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      <dialog
        ref={dialogRef}
        className="contact-dialog"
        aria-labelledby="contact-dialog-title"
        onClick={handleBackdropClick}
      >
        <div className="contact-dialog-inner">
          <button
            type="button"
            className="contact-dialog-close"
            aria-label="Close"
            onClick={() => setOpen(false)}
          >
            <X size={20} strokeWidth={1.5} aria-hidden="true" />
          </button>

          {sent ? (
            <div className="contact-sent" role="status" aria-live="polite">
              <Check className="contact-sent-icon" strokeWidth={1.5} aria-hidden="true" />
              <h3 id="contact-dialog-title">Message sent.</h3>
              <p>Thanks for reaching out. We&rsquo;ll be in touch soon.</p>
              <button type="button" className="btn btn-primary" onClick={() => setOpen(false)}>
                Close
              </button>
            </div>
          ) : (
            <>
              <div className="contact-dialog-header">
                <span className="eyebrow">Get in touch</span>
                <h3 id="contact-dialog-title">Tell us a bit about you.</h3>
                <p>We&rsquo;ll connect you with the right person on our team.</p>
              </div>
              <form
                className="contact-form"
                ref={formRef}
                onSubmit={handleSubmit}
                noValidate
                aria-describedby="dialog-privacy"
              >
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="c-name">Name</label>
                    <input
                      type="text"
                      id="c-name"
                      name="name"
                      autoComplete="name"
                      required
                      placeholder="Your name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="c-email">Email</label>
                    <input
                      type="email"
                      id="c-email"
                      name="email"
                      autoComplete="email"
                      required
                      placeholder="you@example.com"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="c-inquiry">I am a&hellip;</label>
                  <select id="c-inquiry" name="inquiry" defaultValue="" required>
                    <option value="" disabled>
                      Select one
                    </option>
                    <option value="community">Community member</option>
                    <option value="homebuyer">Prospective homebuyer</option>
                    <option value="civic">Civic partner or town official</option>
                    <option value="business">Business or investment partner</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="c-message">Message</label>
                  <textarea
                    id="c-message"
                    name="message"
                    required
                    placeholder="Share a question, idea, or concern."
                  />
                </div>
                <div className="form-submit">
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? 'Sending\u2026' : 'Send message'}
                    {!submitting && <Send className="btn-icon" strokeWidth={1.5} aria-hidden="true" />}
                  </button>
                  <p id="dialog-privacy" className="form-note">
                    We respect your privacy. We will not share your information.
                  </p>
                </div>
              </form>
            </>
          )}
        </div>
      </dialog>
    </section>
  );
}
